import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { IPartialLangDict } from "@/queries/useTranslations";
import type { IContentType, ILanguage } from "@/types";

interface IEdits {
    data: {
        [P in IContentType]?: IPartialLangDict;
    };
}

interface ITranslationsState {
    [index: string]: IEdits;
}

const initialState: ITranslationsState = {};

function newVersionState(): IEdits {
    return {
        data: {},
    };
}

interface IEditPayload {
    version: string;
    contentType: IContentType;
    lang: ILanguage;
    id: string;
    value: string;
}

interface IResetPayload {
    version: string;
    contentType: IContentType;
    lang: ILanguage;
    id: string;
}

const editsSlice = createSlice({
    name: "edits",
    initialState,
    reducers: {
        initEditState(state, action: PayloadAction<string>) {
            const version = action.payload;
            if (!state[version]) {
                state[version] = newVersionState();
            }
        },
        editContent(state, action: PayloadAction<IEditPayload>) {
            const { version, contentType, lang, id, value } = action.payload;
            if (state[version] === undefined) {
                state[version] = newVersionState();
            }
            if (state[version].data[contentType] === undefined) {
                state[version].data[contentType] = {};
            }
            if (state[version].data[contentType][id] === undefined) {
                state[version].data[contentType][id] = {};
            }
            state[version].data[contentType][id][lang] = value;
        },
        resetEdit(state, action: PayloadAction<IResetPayload>) {
            const { version, contentType, lang, id } = action.payload;
            if (state[version] === undefined) {
                return;
            }
            if (state[version].data[contentType] === undefined) {
                return;
            }
            if (state[version].data[contentType][id] === undefined) {
                return;
            }
            delete state[version].data[contentType][id][lang];
            if (!Object.values(state[version].data[contentType][id]).some((v) => !!v)) {
                delete state[version].data[contentType][id];
            }
            if (!Object.values(state[version].data[contentType]).some((v) => !!v)) {
                delete state[version].data[contentType];
            }
        },
        resetAllEdits(state, action: PayloadAction<string>) {
            const version = action.payload;
            state[version].data = {};
        },
    },
});

export const { editContent, resetEdit, resetAllEdits, initEditState } = editsSlice.actions;
export default editsSlice.reducer;
